import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, MapPin, ArrowRight, Mail, Phone, BadgeCheck } from "lucide-react";
import { customerApi, formatStoreError } from "@/lib/customerApi";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { formatPrice } from "@/lib/storefront";

export default function AccountOverview() {
  const { customer } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    customerApi.get("/orders?limit=3")
      .then((r) => setOrders(r.data?.items || []))
      .catch((e) => setError(formatStoreError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-10" data-testid="account-overview">
      {/* Welcome card */}
      <section className="rounded-md border border-[#DDD6C5] bg-white p-6 md:p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E8E2D2] font-display text-lg text-[#1A1A1A]">
            {customer.name?.[0]?.toUpperCase() || "·"}
          </div>
          <div>
            <div className="font-display text-2xl">{customer.name || "Welcome"}</div>
            <div className="text-xs text-[#2D2823]">Member since {customer.created_at?.slice(0, 10) || "—"}</div>
          </div>
        </div>

        <div className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          <Field icon={Mail} label="Email" value={customer.email} />
          <Field icon={Phone} label="Phone" value={customer.phone || "—"} />
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Link to="/account/security" className="inline-flex items-center gap-1.5 rounded-full border border-[#DDD6C5] bg-white px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#1A1A1A] transition-colors hover:bg-[#E8E2D2]">
            <BadgeCheck size={12} /> Update profile
          </Link>
          <Link to="/account/addresses" className="inline-flex items-center gap-1.5 rounded-full border border-[#DDD6C5] bg-white px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#1A1A1A] transition-colors hover:bg-[#E8E2D2]">
            <MapPin size={12} /> Manage addresses
          </Link>
        </div>
      </section>

      {/* Recent orders */}
      <section>
        <div className="mb-4 flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#2D2823]">Recent</div>
            <h2 className="font-display text-2xl">Your orders</h2>
          </div>
          <Link to="/account/orders" className="text-xs text-[#7D8471] underline decoration-[#7D8471] underline-offset-4 hover:text-[#1A1A1A]" data-testid="overview-all-orders">
            View all
          </Link>
        </div>

        {loading ? (
          <div className="h-32 animate-pulse rounded-md bg-[#E8E2D2]" />
        ) : error ? (
          <div className="rounded-md bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
        ) : orders.length === 0 ? (
          <EmptyOrders />
        ) : (
          <div className="divide-y divide-[#DDD6C5] rounded-md border border-[#DDD6C5] bg-white">
            {orders.map((o) => <OrderRow key={o.id} order={o} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-[#DDD6C5] bg-[#F2EDDF] px-3 py-2.5">
      <Icon size={14} className="mt-1 text-[#2D2823]" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#2D2823]">{label}</div>
        <div className="truncate text-sm text-[#1A1A1A]">{value}</div>
      </div>
    </div>
  );
}

export function OrderRow({ order }) {
  const total = formatPrice(order.total || 0, order.currency || "USD");
  const placedAt = order.created_at?.slice(0, 10) || "—";
  return (
    <Link
      to={`/account/orders/${order.id}`}
      className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-[#F2EDDF]"
      data-testid={`acct-order-${order.id}`}
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E8E2D2] text-[#7D8471]">
        <Package size={16} strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-sm text-[#1A1A1A]">{order.order_number}</span>
          <span className="text-xs text-[#2D2823]">·</span>
          <span className="text-xs text-[#2D2823]">{placedAt}</span>
        </div>
        <div className="mt-0.5 truncate text-xs text-[#2D2823]">
          {(order.items || []).length} item{(order.items || []).length === 1 ? "" : "s"} · {humanStatus(order.status)}
        </div>
      </div>
      <div className="text-right">
        <div className="store-nums text-sm font-medium text-[#1A1A1A]">{total}</div>
      </div>
      <ArrowRight size={14} className="text-[#2D2823]" />
    </Link>
  );
}

export function humanStatus(s) {
  const map = {
    new: "Received",
    pending_payment: "Awaiting payment",
    paid: "Paid",
    processing: "Processing",
    packing: "Packing",
    shipped: "Shipped",
    delivered: "Delivered",
    completed: "Completed",
    cancelled: "Cancelled",
    returned: "Returned",
    payment_error: "Payment failed",
    suspicious: "Under review",
  };
  return map[s] || s;
}

function EmptyOrders() {
  return (
    <div className="rounded-md border border-[#DDD6C5] bg-white p-10 text-center" data-testid="orders-empty">
      <Package size={40} strokeWidth={1.2} className="mx-auto mb-4 text-[#2D2823]/30" />
      <h3 className="font-display text-2xl">No orders yet</h3>
      <p className="mt-1 text-sm text-[#2D2823]">When you place your first order it'll appear here.</p>
      <Link to="/catalog" className="mt-6 inline-flex rounded-full bg-[#1A1A1A] px-6 py-3 text-sm text-white transition-colors hover:bg-[#7D8471]">
        Start shopping
      </Link>
    </div>
  );
}
